import { cx } from "@/lib/cx";
import { PB_DISPLAY_URL } from "@/lib/pb";
import { useServerHealth } from "@/lib/useServerHealth";
import { Button } from "@/ui/Button";

/**
 * A quiet badge on the account orb, and a loud banner when the server is gone.
 *
 * Both read the same polled health query, so the app can never show
 * fresh-looking screens while the backend is unreachable.
 */

export type ConnectionDotProps = {
  /**
   * Drop the text and keep the dot where there is no room for a status line.
   */
  compact?: boolean;
  /** Parks the dot on an account button instead of rendering a status row. */
  badge?: boolean;
  className?: string;
};

export function useConnectionPresentation() {
  const { online, pending } = useServerHealth();

  return pending
    ? { dot: "bg-subtle", text: "Connecting…" }
    : online
      // `bg-accent`, the vivid turquoise, not the legacy `bg-focus` alias: a
      // status dot is a **mark**, which is the whole reason the accent split
      // exists. It carries no text, so the 3:1 non-text threshold is the one
      // that applies.
      ? { dot: "bg-accent", text: "Connected" }
      : { dot: "bg-streak", text: "Server unreachable" };
}

export function ConnectionDot({ compact = false, badge = false, className }: ConnectionDotProps) {
  const tone = useConnectionPresentation();

  if (badge) {
    return (
      <span
        className={cx(
          "absolute right-0.5 bottom-0.5 flex size-3 items-center justify-center rounded-full bg-bg",
          className,
        )}
        title={`${tone.text} — ${PB_DISPLAY_URL}`}
      >
        <span aria-hidden className={cx("size-2 rounded-full", tone.dot)} />
        <span className="sr-only">{tone.text}</span>
      </span>
    );
  }

  // The dot alone is a colour-only signal, so the label is hidden rather than
  // removed — the compact form still reads as "Connected" to a screen reader.
  // Deliberately not a live region: this polls, and the banner below is what
  // exists to interrupt someone when the answer actually changes to "no".
  return (
    <div
      className={cx("flex h-7 items-center", compact ? "justify-center" : "gap-2", className)}
      title={`${tone.text} — ${PB_DISPLAY_URL}`}
    >
      <span className={cx("size-2 shrink-0 rounded-full", tone.dot)} aria-hidden />
      <span className={compact ? "sr-only" : "truncate text-caption text-subtle"}>{tone.text}</span>
    </div>
  );
}

export function ConnectionBanner() {
  const { online, pending, checking, recheck } = useServerHealth();

  if (pending || online) {
    return null;
  }

  return (
    <div
      role="alert"
      className="flex min-h-7 flex-wrap items-center justify-between gap-2 border-b border-line bg-streak-soft px-pad-tight sm:px-pad"
    >
      <p className="flex min-w-0 items-center gap-2 text-caption text-ink">
        <span aria-hidden className="size-2 shrink-0 rounded-full bg-streak" />
        <span>
          Cannot reach the Kobo server at <span className="numeric">{PB_DISPLAY_URL}</span>. What
          you see below may be out of date.
        </span>
      </p>
      <Button size="xs" onClick={recheck} loading={checking}>
        Retry
      </Button>
    </div>
  );
}
